export const resolveBoqBomFlow = (pathname = '') => {
  if (pathname.includes('/boq-bom/group-block/')) return 'group-block';
  if (pathname.includes('/boq-bom/common-area/')) return 'common-area';
  if (pathname.includes('/boq-bom/group/')) return 'group';
  if (pathname.includes('/boq-bom/independent/')) return 'independent';
  return 'common-area';
};

export const BOQ_BOM_FLOW_LABELS = {
  'common-area': 'Common Area',
  group: 'Group Accommodation',
  'group-block': 'Group Block',
  independent: 'Independent Accommodation'
};

export const getBoqBomFlowLabel = (pathname) =>
  BOQ_BOM_FLOW_LABELS[resolveBoqBomFlow(pathname)] ?? 'Common Area';

/** Shorter labels for detail header breadcrumbs */
export const BOQ_BOM_BREADCRUMB_FLOW_LABELS = {
  'common-area': 'Common Area',
  group: 'Group Accommodation',
  'group-block': 'Group Block',
  independent: 'Independent'
};

export const getBoqBomBreadcrumbFlowLabel = (pathname) =>
  BOQ_BOM_BREADCRUMB_FLOW_LABELS[resolveBoqBomFlow(pathname)] ?? 'Common Area';

export const getBoqBomBasePath = (pathname, decodedName, params = {}) => {
  const flow = resolveBoqBomFlow(pathname);
  if (flow === 'group-block') {
    const blockId = params.blockId ?? decodedName;
    return `/builder/operations/boq-bom/group-block/${blockId}`;
  }
  return `/builder/operations/boq-bom/${flow}/${encodeURIComponent(decodedName)}`;
};

export const getBoqBomUploadPath = (accommodationType, accommodationId) => {
  const segment = accommodationType === 'GROUP' ? 'group' : 'independent';
  return `/builder/operations/boq-bom/${segment}/${accommodationId}`;
};

export const getBoqBomGroupBlockPath = (blockId) =>
  `/builder/operations/boq-bom/group-block/${blockId}`;

export const getBoqBomDisplayName = (params, locationState) =>
  locationState?.categoryName ||
  (params.accommodationName && decodeURIComponent(params.accommodationName)) ||
  (params.categoryName && decodeURIComponent(params.categoryName)) ||
  'Common Area';

/** Resolves numeric accommodation id from navigation state or route param. */
export const getBoqBomAccommodationId = (params, locationState) => {
  if (locationState?.accommodationId != null) {
    return Number(locationState.accommodationId);
  }
  const param = params?.accommodationName;
  if (param && /^\d+$/.test(String(param))) {
    return Number(param);
  }
  return null;
};

export const getBoqBomQuestionsPath = (pathname, decodedName, params = {}) =>
  `${getBoqBomBasePath(pathname, decodedName, params)}/questions`;

const GROUP_BLOCK_FLOW_API_OPTIONS = {
  intelligenceScope: 'GroupCommon',
  boqGenerateType: 'GroupCommon',
  bomType: 'common',
  bomSyncType: 'GroupCommon',
  syncUsesAccommodationId: true,
  usesProjectId: false
};

const COMMON_AREA_FLOW_API_OPTIONS = {
  intelligenceScope: 'ProjectCommon',
  boqGenerateType: 'ProjectCommon',
  bomType: 'ProjectCommon',
  bomSyncType: 'ProjectCommon',
  syncUsesAccommodationId: false,
  usesProjectId: true
};

const DEFAULT_FLOW_API_OPTIONS = {
  intelligenceScope: null,
  boqGenerateType: null,
  bomType: null,
  bomSyncType: null,
  syncUsesAccommodationId: false
};

/** API params that differ per BOQ/BOM flow. */
export const getBoqBomFlowApiOptions = (pathname) => {
  const flow = resolveBoqBomFlow(pathname);
  if (flow === 'group-block') {
    return GROUP_BLOCK_FLOW_API_OPTIONS;
  }
  if (flow === 'common-area' && pathname.includes('/boq-bom/common-area/')) {
    return COMMON_AREA_FLOW_API_OPTIONS;
  }
  return {
    ...DEFAULT_FLOW_API_OPTIONS,
    bomSyncType: getBomSyncUnitType(pathname)
  };
};

/** Only these four project common work types are used for BOQ/BOM. */
const PROJECT_COMMON_WORK_TYPE_ALIASES = {
  'entrance gate': 'Entrance Gate',
  'enterance gate': 'Entrance Gate',
  garden: 'Garden',
  road: 'Road',
  'boundary walls': 'Boundary Walls'
};

const PROJECT_COMMON_WORK_TYPE_ORDER = [
  'entrance gate',
  'garden',
  'road',
  'boundary walls'
];

export const normalizeProjectCommonWorkTypeName = (name) =>
  String(name || '').trim().toLowerCase();

const getProjectCommonWorkTypeSortKey = (name) => {
  const normalized = normalizeProjectCommonWorkTypeName(name);
  return normalized === 'enterance gate' ? 'entrance gate' : normalized;
};

export const isAllowedProjectCommonWorkType = (name) =>
  getProjectCommonWorkTypeSortKey(name) in PROJECT_COMMON_WORK_TYPE_ALIASES;

export const getProjectCommonWorkTypeDisplayName = (name) => {
  const key = getProjectCommonWorkTypeSortKey(name);
  return PROJECT_COMMON_WORK_TYPE_ALIASES[key] ?? name;
};

/** Filter API work types to Boundary Walls, Entrance Gate, Garden, and Road only. */
export const filterProjectCommonWorkTypes = (workTypes) => {
  const list = Array.isArray(workTypes) ? workTypes : [];
  return list
    .filter((item) => isAllowedProjectCommonWorkType(item?.name))
    .sort(
      (a, b) =>
        PROJECT_COMMON_WORK_TYPE_ORDER.indexOf(getProjectCommonWorkTypeSortKey(a.name)) -
        PROJECT_COMMON_WORK_TYPE_ORDER.indexOf(getProjectCommonWorkTypeSortKey(b.name))
    )
    .map((item) => ({
      id: item.id,
      name: getProjectCommonWorkTypeDisplayName(item.name),
      apiName: item.name
    }));
};

/** Work type ids from navigation state — only the four allowed common area types. */
export const getBoqBomWorkTypeIds = (locationState) =>
  filterProjectCommonWorkTypes(locationState?.workTypes)
    .map((item) => Number(item.id))
    .filter((id) => Number.isFinite(id));

/** Resolve flow API options from route or persisted navigation state. */
export const resolveBoqBomFlowApiOptions = (pathname, locationState) =>
  locationState?.flowApiOptions ?? getBoqBomFlowApiOptions(pathname);

/** Group block: POST /boq/extractData & GET /boq/questions (accommodation_id). */
export const buildBoqIntelligenceOptions = (flowApiOptions) => {
  if (!flowApiOptions?.intelligenceScope) return {};
  return { intelligenceScope: flowApiOptions.intelligenceScope };
};

/** Project common: GET /boq/questions?project_id=&intelligence_scope=ProjectCommon */
export const buildBoqQuestionsOptions = (flowApiOptions, { projectId } = {}) => {
  if (flowApiOptions?.usesProjectId) {
    return {
      projectId,
      intelligenceScope: flowApiOptions.intelligenceScope ?? 'ProjectCommon'
    };
  }
  return buildBoqIntelligenceOptions(flowApiOptions);
};

/** BOM combination count for project common — one per allowed work type. */
export const getProjectCommonCombinationCount = (workTypeIds = []) => {
  const count = workTypeIds.filter((id) => Number.isFinite(id)).length;
  return count > 0 ? count : PROJECT_COMMON_WORK_TYPE_ORDER.length;
};

const resolveProjectCommonWorkTypeIds = ({ workTypeIds, workTypes } = {}) => {
  if (Array.isArray(workTypes) && workTypes.length) {
    return filterProjectCommonWorkTypes(workTypes)
      .map((item) => Number(item.id))
      .filter((id) => Number.isFinite(id));
  }
  if (Array.isArray(workTypeIds)) {
    return workTypeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
  }
  return [];
};

/** POST /boq/extractData — project common area uses project_id + work_type_ids. */
export const buildBoqExtractDataOptions = (
  flowApiOptions,
  { projectId, workTypeIds, workTypes } = {}
) => {
  if (flowApiOptions?.usesProjectId) {
    return {
      projectId,
      intelligenceScope: flowApiOptions.intelligenceScope ?? 'ProjectCommon',
      workTypeIds: resolveProjectCommonWorkTypeIds({ workTypeIds, workTypes })
    };
  }
  return buildBoqIntelligenceOptions(flowApiOptions);
};

/** GET /boq?accommodation_id= — group block common uses type GroupCommon. */
export const buildGetBoqOptions = (flowApiOptions) => {
  if (!flowApiOptions?.boqGenerateType) return {};
  return { type: flowApiOptions.boqGenerateType };
};

/** POST /boq/generate — project common uses type ProjectCommon + project_id + work_type_ids. */
export const buildGenerateBoqOptions = (
  flowApiOptions,
  { projectId, workTypeIds, workTypes } = {}
) => {
  if (flowApiOptions?.usesProjectId) {
    return {
      type: flowApiOptions.boqGenerateType ?? 'ProjectCommon',
      projectId,
      workTypeIds: resolveProjectCommonWorkTypeIds({ workTypeIds, workTypes })
    };
  }
  if (!flowApiOptions?.boqGenerateType) return {};
  return { type: flowApiOptions.boqGenerateType };
};

/** POST /boq/bom/generate — project common uses type ProjectCommon + project_id. */
export const buildGenerateBomOptions = (flowApiOptions, { projectId } = {}) => {
  if (flowApiOptions?.usesProjectId && projectId != null) {
    return {
      type: flowApiOptions.bomType ?? 'ProjectCommon',
      projectId
    };
  }
  if (!flowApiOptions?.bomType) return {};
  return { type: flowApiOptions.bomType };
};

/** POST /boq/bom/applyCosting — project common uses type ProjectCommon + project_id. */
export const buildApplyBomCostingPayload = (payload, flowApiOptions, { projectId } = {}) => {
  if (flowApiOptions?.usesProjectId && projectId != null) {
    const { accommodation_id: _accommodationId, ...rest } = payload;
    return {
      ...rest,
      type: flowApiOptions.bomType ?? 'ProjectCommon',
      project_id: projectId
    };
  }
  const bomOptions = buildGenerateBomOptions(flowApiOptions);
  return bomOptions.type ? { ...payload, type: bomOptions.type } : payload;
};

/** POST /boq/bom/sync */
export const buildSyncBomPayload = (
  accommodationId,
  flowApiOptions,
  { unitId, projectId, combinationName } = {}
) => {
  if (flowApiOptions?.usesProjectId && flowApiOptions?.bomSyncType) {
    if (projectId == null) return null;
    return {
      type: flowApiOptions.bomSyncType,
      project_id: projectId,
      combination_name: combinationName ?? 'Standard',
      replace_existing: true,
      use_ai_assignment: false
    };
  }

  if (flowApiOptions?.syncUsesAccommodationId && flowApiOptions?.bomSyncType) {
    return {
      type: flowApiOptions.bomSyncType,
      accommodation_id: accommodationId
    };
  }

  if (flowApiOptions?.bomSyncType && unitId != null) {
    return {
      type: flowApiOptions.bomSyncType,
      unit_id: unitId,
      use_ai_assignment: false,
      replace_existing: true
    };
  }

  return null;
};

/** `type` for `POST /boq/bom/sync` from BOQ/BOM flow (unit-based flows). */
export const getBomSyncUnitType = (pathname) => {
  const flow = resolveBoqBomFlow(pathname);
  if (flow === 'independent') return 'IndependentUnit';
  if (flow === 'group') return 'GroupUnit';
  return null;
};
